<template>
  <div id="ringtone_selector" class="form_col gap-2">
    <span class="secondary_color text-[14px]">Ringtone for incoming calls</span>

    <CustomDropdown
      ref="dropdownRef"
      width="w-full"
      max-height="400px"
      gap="5px"
      position="start"
      dropdownCSS="p-0"
      @close="handleDropdownClose"
    >
      <template #trigger>
        <div class="input_medium">
          <div class="flex items-center flex-1 min-w-0 gap-2">
            <template v-if="isLoading">
              <span class="secondary_color">Loading ringtones...</span>
            </template>
            <template v-else-if="selectedRingtone">
              <RingtoneItem
                :ringtone="selectedRingtone"
                :is-playing="isRingtonePlaying(selectedRingtone.id)"
                @play="handleTogglePlay"
              />
            </template>
            <template v-else>
              <span class="text-sm text_color">Default</span>
            </template>
          </div>

          <div class="flex items-center ml-2">
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="arrow"
              class="size-4"
            />
          </div>
        </div>
      </template>

      <template #default>
        <div class="flex flex-col">
          <div class="flex flex-col max-h-[300px] overflow-y-auto custom_scrollbar pl-2 py-2">
            <div
              :class="[
                'dropdown_item flex-shrink-0 flex items-center flex-row px-3 py-2 rounded-lg cursor-pointer',
                { dropdown_item_selected: !selectedRingtone },
              ]"
              @click="handleNoneSelect"
            >
              <span class="text-sm text_color">Default</span>
            </div>

            <div
              v-if="systemRingtones.length"
              class="flex flex-col mt-1"
            >
              <span
                class="text-[11px] font-semibold uppercase secondary_color px-3 py-1 tracking-wider"
              >
                System Ringtones
              </span>
              <div
                v-for="ringtone in systemRingtones"
                :key="`system-${ringtone.id}`"
                :class="[
                  'dropdown_item flex-shrink-0 flex items-center flex-row justify-between px-3 py-2 rounded-lg cursor-pointer',
                  { dropdown_item_selected: isRingtoneSelected(ringtone.id) },
                ]"
                @click="handleRingtoneSelect(ringtone)"
              >
                <RingtoneItem
                  :ringtone="ringtone"
                  :is-playing="isRingtonePlaying(ringtone.id)"
                  @play="handleTogglePlay"
                />
              </div>
            </div>

            <div
              v-if="customRingtones.length"
              class="flex flex-col mt-1"
            >
              <span
                class="text-[11px] font-semibold uppercase secondary_color px-3 py-1 tracking-wider"
              >
                Custom Ringtones
              </span>
              <div
                v-for="ringtone in customRingtones"
                :key="`custom-${ringtone.id}`"
                :class="[
                  'dropdown_item flex-shrink-0 flex items-center flex-row justify-between px-3 py-2 rounded-lg cursor-pointer',
                  { dropdown_item_selected: isRingtoneSelected(ringtone.id) },
                ]"
                @click="handleRingtoneSelect(ringtone)"
              >
                <RingtoneItem
                  :ringtone="ringtone"
                  :is-playing="isRingtonePlaying(ringtone.id)"
                  @play="handleTogglePlay"
                />
                <ThemeImage
                  :icon="imageNames.DELETE_GREY"
                  alt="delete_icon"
                  class="size-4 cursor-pointer"
                  @click.stop="handleDeleteRingtone(ringtone)"
                />
              </div>
            </div>

            <div
              v-if="!isLoading && !systemRingtones.length && !customRingtones.length"
              class="flex flex-col items-center justify-center py-4 px-3"
            >
              <span class="text-sm secondary_color text-center">
                No ringtones available
              </span>
            </div>
          </div>

          <div
            class="flex items-center gap-2 px-3 py-2 border-t border_8 cursor-pointer dropdown_item"
            @click="handleAddNewClick"
          >
            <ThemeImage
              :icon="imageNames.PLUS_GREY"
              alt="add_icon"
              class="size-4"
            />
            <span class="text-sm text_color">Add New Ringtone</span>
          </div>
        </div>
      </template>
    </CustomDropdown>

    <AddRingtoneModal
      v-model:visible="isAddModalVisible"
      source="ringtone_selector"
      @update:success="handleRingtoneCreated"
    />

    <ConfirmationModal
      v-model:visible="deleteModalVisible"
      title="Delete Ringtone"
      :message="`Are you sure you want to delete '${ringtoneToDelete?.recording_name}'?`"
      :loading="isDeleting"
      type="delete"
      confirm-text="Delete"
      loading-text="Deleting..."
      @confirm="handleDeleteConfirm"
      @cancel="deleteModalVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { message } from "ant-design-vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import RingtoneItem from "@/src/views/calls/dialer_settings/RingtoneItem.vue";
import AddRingtoneModal from "@/src/views/calls/dialer_settings/AddRingtoneModal.vue";
import CallService from "@/src/api/CallService.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const dropdownRef = ref(null);
const ringtones = ref([]);
const isLoading = ref(false);
const isDeleting = ref(false);
const isAddModalVisible = ref(false);
const deleteModalVisible = ref(false);
const ringtoneToDelete = ref(null);

const playingRingtoneId = ref(null);
const audioElement = ref(null);

const stopPlayback = () => {
  if (audioElement.value) {
    try {
      audioElement.value.pause();
      audioElement.value.src = "";
    } catch (error) {
      console.error("Error stopping ringtone playback:", error);
    }
    audioElement.value = null;
  }
  playingRingtoneId.value = null;
};

const systemRingtones = computed(() =>
  ringtones.value.filter((ringtone) => ringtone.is_default),
);

const customRingtones = computed(() =>
  ringtones.value.filter((ringtone) => !ringtone.is_default),
);

const selectedRingtone = computed(
  () => ringtones.value.find((ringtone) => ringtone.is_primary) || null,
);

const isRingtoneSelected = (ringtoneId) =>
  selectedRingtone.value?.id === ringtoneId;

const isRingtonePlaying = (ringtoneId) =>
  playingRingtoneId.value === ringtoneId;

const fetchRingtones = async () => {
  try {
    isLoading.value = true;
    const response = await CallService.getIncomingCallRingtones();
    ringtones.value = response?.ringtones || [];
  } catch (error) {
    console.error("Error fetching ringtones:", error);
    message.error(
      error?.response?.data?.error || "Failed to load ringtones",
    );
  } finally {
    isLoading.value = false;
  }
};

const handleNoneSelect = async () => {
  if (!selectedRingtone.value) {
    dropdownRef.value?.closeDropdown();
    return;
  }

  const previousRingtones = [...ringtones.value];

  ringtones.value = ringtones.value.map((item) => ({
    ...item,
    is_primary: false,
  }));

  try {
    await CallService.removeIncomingCallRingtonePrimary();
    message.success("Ringtone removed successfully");
    dropdownRef.value?.closeDropdown();
  } catch (error) {
    console.error("Error removing primary ringtone:", error);
    ringtones.value = previousRingtones;
    message.error(
      error?.response?.data?.error || "Failed to remove ringtone",
    );
  }
};

const handleRingtoneSelect = async (ringtone) => {
  if (!ringtone?.id || selectedRingtone.value?.id === ringtone.id) {
    dropdownRef.value?.closeDropdown();
    return;
  }

  const previousPrimaryId = selectedRingtone.value?.id;

  ringtones.value = ringtones.value.map((item) => ({
    ...item,
    is_primary: item.id === ringtone.id,
  }));

  try {
    await CallService.updateIncomingCallRingtone(ringtone.id, {
      is_primary: true,
    });
    message.success("Ringtone updated successfully");
    dropdownRef.value?.closeDropdown();
  } catch (error) {
    console.error("Error updating ringtone:", error);
    ringtones.value = ringtones.value.map((item) => ({
      ...item,
      is_primary: item.id === previousPrimaryId,
    }));
    message.error(
      error?.response?.data?.error || "Failed to update ringtone",
    );
  }
};

const handleDeleteRingtone = (ringtone) => {
  if (!ringtone?.id) return;
  ringtoneToDelete.value = ringtone;
  deleteModalVisible.value = true;
};

const handleDeleteConfirm = async () => {
  const ringtone = ringtoneToDelete.value;
  if (!ringtone?.id) return;

  try {
    isDeleting.value = true;
    if (playingRingtoneId.value === ringtone.id) {
      stopPlayback();
    }
    await CallService.deleteIncomingCallRingtone(ringtone.id);
    ringtones.value = ringtones.value.filter(
      (item) => item.id !== ringtone.id,
    );
    message.success("Ringtone deleted successfully");
    deleteModalVisible.value = false;
    ringtoneToDelete.value = null;
  } catch (error) {
    console.error("Error deleting ringtone:", error);
    message.error(
      error?.response?.data?.error || "Failed to delete ringtone",
    );
  } finally {
    isDeleting.value = false;
  }
};

const handleTogglePlay = (ringtone) => {
  try {
    if (!ringtone?.ringtone_url || !ringtone?.id) return;

    if (playingRingtoneId.value === ringtone.id) {
      stopPlayback();
      return;
    }

    stopPlayback();

    const ringtoneId = ringtone.id;
    const audio = new Audio(ringtone.ringtone_url);

    audio.onended = () => {
      if (playingRingtoneId.value === ringtoneId) {
        playingRingtoneId.value = null;
        audioElement.value = null;
      }
    };

    audioElement.value = audio;
    playingRingtoneId.value = ringtoneId;

    audio.play().catch((error) => {
      console.warn("Error playing ringtone:", error);
      if (playingRingtoneId.value === ringtoneId) {
        playingRingtoneId.value = null;
        audioElement.value = null;
        message.error("Failed to play ringtone");
      }
    });
  } catch (error) {
    console.error("Error toggling ringtone playback:", error);
  }
};

const handleAddNewClick = () => {
  dropdownRef.value?.closeDropdown();
  isAddModalVisible.value = true;
};

const handleRingtoneCreated = (newRingtone) => {
  if (!newRingtone?.id) {
    fetchRingtones();
    return;
  }

  if (newRingtone.is_primary) {
    ringtones.value = ringtones.value.map((item) => ({
      ...item,
      is_primary: false,
    }));
  }
  ringtones.value = [...ringtones.value, newRingtone];
};

const handleDropdownClose = () => {
  stopPlayback();
};

onMounted(() => {
  fetchRingtones();
});

onBeforeUnmount(() => {
  stopPlayback();
});
</script>
